import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PageTypeConfiguration } from "./PageTypesCreateContainer";

/**
 * SCRUM-2267: the create-page wizard's step and page state, written as a
 * createSlice like every reducer in this client (SCRUM-1954) and mounted
 * LOCALLY with `useReducer(pageFormWizard.reducer, ...)` in PageFormContainer,
 * next to the validation flags of SCRUM-2266. The SCRUM-2127 pattern.
 *
 * The six values used to be six `useState` calls that advanced together:
 * - `currentStep`: the wizard step (0 page type, 1 template and header, 2 save);
 * - `page`: the page header, template and content. Its page_configuration is
 *   not kept here: PageFormContainer derives it from `pageTypeSelection` (SCRUM-2206);
 * - `pageTypeSelection`: the page-type step's pick, as onConfigurationChange reports it;
 * - `templateStepKey` / `pageTypeStepKey`: the template step (IndexTemplateSelector)
 *   and the page-type step (PageTypesCreateContainer) start over when theirs changes;
 * - `typedEntityPath`: the routing path the editor typed for a category /
 *   sub_category page, "" while untouched (SCRUM-1575).
 */

/** The page draft the wizard builds; page_configuration grows per page type. */
export type PageFormState = {
  page_template: unknown;
  page_content: unknown[];
  page_configuration: { type?: string; path?: string; object_target?: unknown; [key: string]: unknown };
  sub_pages: boolean;
  publish_statement: boolean;
  meta_description: string;
  global_title: string;
  robots: string;
  skip_product_page: boolean;
  [key: string]: unknown;
};

/**
 * The page-type step's pick: the type, its object (a path string or an entity)
 * and the path the step reported with it.
 */
export type PageTypeSelection = {
  type: PageTypeConfiguration["type"];
  object: any; // any: polymorphic per type, read field by field in computeConfiguration
  path: string;
};

const initialPageState: PageFormState = {
  page_template: null,
  page_content: [],
  page_configuration: {},
  sub_pages: true,
  publish_statement: false,
  meta_description: "",
  global_title: "",
  robots: "",
  skip_product_page: false,
};

type PageFormWizardState = {
  currentStep: number;
  page: PageFormState;
  pageTypeSelection: PageTypeSelection | null;
  templateStepKey: number;
  pageTypeStepKey: number;
  typedEntityPath: string;
};

const initialState: PageFormWizardState = {
  currentStep: 0,
  page: initialPageState,
  pageTypeSelection: null,
  templateStepKey: 0,
  pageTypeStepKey: 0,
  typedEntityPath: "",
};

export const pageFormWizard = createSlice({
  name: "pageFormWizard",
  initialState,
  reducers: {
    stepAdvanced(state) {
      state.currentStep += 1;
    },
    stepBack(state) {
      state.currentStep = Math.max(0, state.currentStep - 1);
    },
    // One page field; the caller passes the value already in its stored form.
    pageFieldChanged(state, action: PayloadAction<{ field: string; value: unknown }>) {
      state.page[action.payload.field] = action.payload.value;
    },
    // A new page-type pick: the template step starts over, the page header and
    // content go back to empty, and only the entity types keep a typed path.
    pageTypePicked(state, action: PayloadAction<PageTypeSelection>) {
      const selection = action.payload;
      state.templateStepKey += 1;
      state.typedEntityPath = ["category", "sub_category"].includes(selection.type)
        ? selection.path
        : "";
      state.pageTypeSelection = selection;
      state.page = initialPageState;
    },
    // After a successful save both steps start over (SCRUM-2182: the page-type
    // step only here, not on a pick) and the wizard is back at its start.
    wizardSaved(state) {
      state.templateStepKey += 1;
      state.pageTypeStepKey += 1;
      state.page = initialPageState;
      state.pageTypeSelection = null;
      state.typedEntityPath = "";
      state.currentStep = 0;
    },
  },
});
